import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Modal from '../components/Modal';
import Button from '../components/Button';
import TableScrollContainer from '../components/TableScrollContainer';
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../utils/currency';
import { formatServiceName } from '../utils/formatServiceName';
import './Servicos.css';

const SERVICE_CATEGORIES = ['Cílios', 'Sobrancelha', 'Maquiagem', 'Limpeza', 'Depilação', 'Curso'];

function Servicos() {
  const [servicos, setServicos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ nome: '', categoria: 'Cílios', descricao: '', valor_padrao: '', duracao_minutos: 60 });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const formatDuration = (min) => {
    const val = Number(min) || 60;
    if (val >= 60 && val % 60 === 0) {
      return `${val / 60}h`;
    }
    if (val > 60) {
      const h = Math.floor(val / 60);
      const m = val % 60;
      return `${h}h ${m}min`;
    }
    return `${val} min`;
  };

  useEffect(() => {
    fetchServicos();
  }, []);

  const fetchServicos = async () => {
    try {
      const response = await axios.get('/api/servicos');
      setServicos(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const valorNormalizado = parseCurrencyInput(formData.valor_padrao);
    if (formData.valor_padrao && !valorNormalizado) {
      alert('Informe um valor válido.');
      return;
    }

    const payload = {
      ...formData,
      valor_padrao: valorNormalizado || '0.00',
      duracao_minutos: Number(formData.duracao_minutos) || 60,
    };

    try {
      if (editingId) {
        await axios.put(`/api/servicos/${editingId}`, payload);
      } else {
        await axios.post('/api/servicos', payload);
      }
      fetchServicos();
      setShowModal(false);
      setFormData({ nome: '', categoria: 'Cílios', descricao: '', valor_padrao: '', duracao_minutos: 60 });
      setEditingId(null);
    } catch (error) {
      console.error('Erro ao salvar serviço:', error);
    }
  };

  const handleEdit = (servico) => {
    setFormData({
      ...servico,
      categoria: servico.categoria || 'Cílios',
      valor_padrao: formatCurrencyInput(servico.valor_padrao),
      duracao_minutos: servico.duracao_minutos || 60,
    });
    setEditingId(servico.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza?')) {
      try {
        await axios.delete(`/api/servicos/${id}`);
        fetchServicos();
      } catch (error) {
        console.error('Erro ao deletar serviço:', error);
      }
    }
  };

  return (
    <div className="servicos">
      <div className="header-section">
        <h1>Serviços</h1>
        <Button
          variant="primary"
          onClick={() => {
            setFormData({ nome: '', categoria: 'Cílios', descricao: '', valor_padrao: '', duracao_minutos: 60 });
            setEditingId(null);
            setShowModal(true);
          }}
        >
          <FiPlus /> Novo Serviço
        </Button>
      </div>

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <div className="card">
          <TableScrollContainer rows={servicos.length}>
            <table>
            <thead>
              <tr>
                <th>Serviço</th>
                <th>Categoria</th>
                <th>Duração</th>
                <th>Descrição</th>
                <th>Valor Padrão</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {servicos.map((servico) => (
                <tr key={servico.id}>
                  <td><strong>{formatServiceName(servico.nome)}</strong></td>
                  <td>{servico.categoria || '-'}</td>
                  <td>{formatDuration(servico.duracao_minutos)}</td>
                  <td>{servico.descricao || '-'}</td>
                  <td>{formatCurrency(servico.valor_padrao)}</td>
                  <td className="actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(servico)}
                      title="Editar"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(servico.id)}
                      title="Deletar"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </TableScrollContainer>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingId(null);
          setFormData({ nome: '', categoria: 'Cílios', descricao: '', valor_padrao: '', duracao_minutos: 60 });
        }}
        title={editingId ? 'Editar Serviço' : 'Novo Serviço'}
      >
        <form onSubmit={handleSubmit} className="form">
          <label>
            Nome do Serviço *
            <input
              type="text"
              required
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Ex: Maquiagem, Sobrancelha, etc"
            />
          </label>

          <label>
            Categoria *
            <select
              required
              value={formData.categoria}
              onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
            >
              {SERVICE_CATEGORIES.map((categoria) => (
                <option key={categoria} value={categoria}>{categoria}</option>
              ))}
            </select>
          </label>

          <label>
            Duração Estimada *
            <select
              required
              value={formData.duracao_minutos || 60}
              onChange={(e) => setFormData({ ...formData, duracao_minutos: Number(e.target.value) })}
            >
              <option value={30}>30 minutos</option>
              <option value={45}>45 minutos</option>
              <option value={60}>60 minutos (1 hora)</option>
              <option value={90}>90 minutos (1h 30min)</option>
              <option value={120}>120 minutos (2 horas)</option>
              <option value={150}>150 minutos (2h 30min)</option>
              <option value={180}>180 minutos (3 horas)</option>
            </select>
          </label>

          <label>
            Descrição
            <textarea
              value={formData.descricao || ''}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descreva o serviço"
              rows="3"
            />
          </label>

          <label>
            Valor Padrão (R$)
            <input
              type="text"
              inputMode="decimal"
              value={formData.valor_padrao}
              onChange={(e) => setFormData({ ...formData, valor_padrao: formatCurrencyInput(e.target.value) })}
              placeholder="0,00"
            />
          </label>

          <div className="form-actions">
            <Button type="submit" variant="primary">
              Salvar
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingId(null);
                setFormData({ nome: '', categoria: 'Cílios', descricao: '', valor_padrao: '', duracao_minutos: 60 });
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Servicos;
